import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

// Task 18 — the browser never talks to the payroll server and never polls continuously; it only ever reads
// this app's own stored obligations (GET /payroll/pending), fetched once when an admin session is
// confirmed. Every other refresh is a deliberate pull triggered by a person: opening the pending panel, or
// just having created a voucher — never a timer. Admin-only because the underlying data (individual
// advance/loan amounts and employee names, and pay-run cash totals) is exactly the sensitive, employee-
// level payroll detail this app's two-role model has no finer grain to hand a cashier "view but not act on".
const PayrollPendingContext = createContext(null);

export function PayrollPendingProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [configured, setConfigured] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Wave review, cross-cutting: the catch below used to set exactly the same state a clean "not configured"
  // 200 response does, so a deployment that never touched payroll config and one whose payroll config is
  // wrong (or whose payroll server is unreachable) were indistinguishable to the admin — both showed
  // nothing at all. GET /payroll/pending answers `{configured: false}` with a 200 for the *deliberately*
  // unconfigured case (server/index.php), which lands in the try block below and never sets this; only a
  // real failure (network error, non-2xx, a malformed response) reaches the catch and sets it. This does
  // not change the "invisible unless there is something real to show" rule for an install that never turns
  // payroll on — it only gives a signal for the case that rule was never meant to hide.
  const [fetchFailed, setFetchFailed] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/pending");
      setConfigured(!!data.configured);
      setItems(data.items || []);
      setFetchFailed(false);
    } catch {
      // Fails closed and silent (no toast) — a transient error here must never interrupt someone for a
      // feature that is meant to be invisible unless there is something real to show — but it is no longer
      // silent to every surface: fetchFailed lets PayrollPendingIndicator show a quiet, admin-only signal
      // that is genuinely absent for a deployment that simply never configured payroll.
      setConfigured(false);
      setItems([]);
      setFetchFailed(true);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) refresh();
    else { setConfigured(false); setItems([]); setFetchFailed(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const createVoucher = useCallback(async (id) => {
    const { data } = await api.post(`/payroll/${id}/voucher`, {});
    await refresh();
    return data;
  }, [refresh]);

  // A deliberate, admin-initiated pull of the payroll server — the same POST /payroll/sync a scheduled
  // cron tick already calls (see payroll_cron.php) — exposed here purely as a convenience so an admin who
  // just approved something in payroll doesn't have to wait for the next tick. Still server-side only, and
  // still nothing the browser calls on its own timer.
  const syncNow = useCallback(async () => {
    const { data } = await api.post("/payroll/sync", {});
    await refresh();
    return data;
  }, [refresh]);

  return (
    <PayrollPendingContext.Provider value={{ configured, items, count: items.length, loading, fetchFailed, refresh, createVoucher, syncNow }}>
      {children}
    </PayrollPendingContext.Provider>
  );
}

export const usePayrollPending = () => useContext(PayrollPendingContext);
